import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Colours from '../../../design-system/colours';

interface OvCopyToClipboardButtonProps {
  text: string;
  label: string;
  copiedLabel?: string;
  failedLabel?: string;
}

type CopyResult = 'idle' | 'copied' | 'failed';

const OvCopyToClipboardButton: FC<OvCopyToClipboardButtonProps> = ({
  text,
  label,
  copiedLabel,
  failedLabel,
}) => {
  const { t } = useTranslation();
  const [copyResult, setCopyResult] = useState<CopyResult>('idle');

  const getFeedback = () => {
    switch (copyResult) {
      case 'idle':
        return '';

      case 'copied':
        return copiedLabel ?? t('common.actions.copied');

      case 'failed':
        return failedLabel ?? t('common.actions.copyFailed');

      default: {
        const unhandledResult: never = copyResult;

        return unhandledResult;
      }
    }
  };

  const feedback = getFeedback();

  return (
    <>
      <CopyToClipboard
        text={text}
        onCopy={(_, isCopied) => setCopyResult(isCopied ? 'copied' : 'failed')}
      >
        <Tooltip title={feedback || label}>
          <StyledIconButton
            aria-label={label}
            size="small"
            onBlur={() => setCopyResult('idle')}
          >
            <ContentCopyIcon fontSize="inherit" />
          </StyledIconButton>
        </Tooltip>
      </CopyToClipboard>
      <VisuallyHidden role="status">{feedback}</VisuallyHidden>
    </>
  );
};

export default OvCopyToClipboardButton;

const StyledIconButton = styled(IconButton)`
  && {
    color: ${Colours.OV_BASE};
    font-size: 0.9375rem;
    padding: 0.25rem;
    margin-left: 0.125rem;
    vertical-align: text-bottom;

    &:focus-visible {
      outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    }
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;
